import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { format, addDays, isSameDay, startOfWeek } from "date-fns";
import { de } from "date-fns/locale";
import { Plus } from "lucide-react";

// ─── Config ─────────────────────────────────────────────
const START_HOUR = 6;
const END_HOUR = 22;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const HOUR_PX = 60; // pixels per hour
const HALF_HOUR_PX = HOUR_PX / 2;
const GRID_HEIGHT = TOTAL_HOURS * HOUR_PX;
const SNAP_MINUTES = 15; // snap to 15-min intervals

const PROJECT_COLORS = [
  { bg: "bg-blue-200/80", border: "border-blue-400", text: "text-blue-900", hex: "#93c5fd" },
  { bg: "bg-emerald-200/80", border: "border-emerald-400", text: "text-emerald-900", hex: "#6ee7b7" },
  { bg: "bg-purple-200/80", border: "border-purple-400", text: "text-purple-900", hex: "#c4b5fd" },
  { bg: "bg-orange-200/80", border: "border-orange-400", text: "text-orange-900", hex: "#fdba74" },
  { bg: "bg-pink-200/80", border: "border-pink-400", text: "text-pink-900", hex: "#f9a8d4" },
  { bg: "bg-teal-200/80", border: "border-teal-400", text: "text-teal-900", hex: "#5eead4" },
  { bg: "bg-indigo-200/80", border: "border-indigo-400", text: "text-indigo-900", hex: "#a5b4fc" },
  { bg: "bg-yellow-200/80", border: "border-yellow-400", text: "text-yellow-900", hex: "#fde68a" },
];

const colorCache = {};
const getColor = (projectId) => {
  if (!colorCache[projectId]) {
    colorCache[projectId] = PROJECT_COLORS[Object.keys(colorCache).length % PROJECT_COLORS.length];
  }
  return colorCache[projectId];
};

// Convert time to Y position
const timeToY = (date) => {
  const h = date.getHours() + date.getMinutes() / 60;
  return Math.max(0, (h - START_HOUR) * HOUR_PX);
};

// Convert Y position to hours/minutes (snapped)
const yToTime = (y) => {
  const rawMinutes = (y / HOUR_PX) * 60 + START_HOUR * 60;
  const snapped = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
  const h = Math.floor(snapped / 60);
  const m = snapped % 60;
  return { hours: Math.max(START_HOUR, Math.min(END_HOUR, h)), minutes: m };
};

const fmtTime = (h, m) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

// ─── Component ──────────────────────────────────────────
export default function WeekCalendar({ entries, weekMonday, projects, onEntryUpdate, onEntryCreate, onEntryClick }) {
  const gridRef = useRef(null);
  const [dragState, setDragState] = useState(null); // { entryId, type: 'move'|'resize', startY, origTop, origHeight, origDayIdx, currentTop, currentHeight, currentDayIdx }
  const [hoverSlot, setHoverSlot] = useState(null); // { dayIdx, y }
  const [ghostEntry, setGhostEntry] = useState(null); // preview during drag-create
  const [createDrag, setCreateDrag] = useState(null); // { dayIdx, startY }

  const days = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i)),
    [weekMonday]
  );

  // Map entries to their day columns
  const dayEntries = useMemo(() => {
    const result = Array.from({ length: 7 }, () => []);
    entries.forEach((entry) => {
      if (!entry.start_time || !entry.end_time) return;
      const start = new Date(entry.start_time);
      days.forEach((day, idx) => {
        if (isSameDay(start, day)) {
          const end = new Date(entry.end_time);
          const top = timeToY(start);
          const bottom = timeToY(end);
          const height = Math.max(bottom - top, HALF_HOUR_PX / 2);
          result[idx].push({ ...entry, _top: top, _height: height, _start: start, _end: end });
        }
      });
    });
    return result;
  }, [entries, days]);

  // ─── Drag: Move ─────────────────────────────────────
  const handleEntryMouseDown = useCallback((e, entry, dayIdx) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();

    const rect = gridRef.current.getBoundingClientRect();
    const startY = e.clientY;
    const startX = e.clientX;

    setDragState({
      entryId: entry.id,
      entry,
      type: "move",
      startY,
      startX,
      origTop: entry._top,
      origHeight: entry._height,
      origDayIdx: dayIdx,
      currentTop: entry._top,
      currentHeight: entry._height,
      currentDayIdx: dayIdx,
      gridLeft: rect.left,
      gridWidth: rect.width,
      hasMoved: false, // Track if user actually dragged
    });
  }, []);

  // ─── Drag: Resize ──────────────────────────────────
  const handleResizeMouseDown = useCallback((e, entry, dayIdx) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();

    setDragState({
      entryId: entry.id,
      entry,
      type: "resize",
      startY: e.clientY,
      origTop: entry._top,
      origHeight: entry._height,
      origDayIdx: dayIdx,
      currentTop: entry._top,
      currentHeight: entry._height,
      currentDayIdx: dayIdx,
      hasMoved: false, // Track if user actually dragged
    });
  }, []);

  // ─── Drag: Create (click on empty space) ───────────
  const handleGridMouseDown = useCallback((e, dayIdx) => {
    if (e.button !== 0 || dragState) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + e.currentTarget.scrollTop;

    setCreateDrag({ dayIdx, startY: y, currentY: y });
  }, [dragState]);

  // ─── Global mouse move/up ──────────────────────────
  useEffect(() => {
    if (!dragState && !createDrag) return;

    const handleMouseMove = (e) => {
      if (dragState) {
        const dy = e.clientY - dragState.startY;
        const dx = dragState.type === "move" ? e.clientX - dragState.startX : 0;
        
        // Check if user has moved enough to count as a drag (threshold: 5px)
        const hasMoved = Math.abs(dy) > 5 || Math.abs(dx) > 5;

        if (dragState.type === "move") {
          // Calculate new day based on X position
          const timeColWidth = (dragState.gridWidth - 48) / 7; // 48px for time labels
          const newDayIdx = Math.max(0, Math.min(6,
            dragState.origDayIdx + Math.round(dx / timeColWidth)
          ));

          const newTop = Math.max(0, Math.min(GRID_HEIGHT - dragState.origHeight,
            dragState.origTop + dy
          ));
          // Snap
          const snappedTop = Math.round(newTop / (SNAP_MINUTES / 60 * HOUR_PX)) * (SNAP_MINUTES / 60 * HOUR_PX);

          setDragState((prev) => ({ ...prev, currentTop: snappedTop, currentDayIdx: newDayIdx, hasMoved: prev.hasMoved || hasMoved }));
        } else if (dragState.type === "resize") {
          const newHeight = Math.max(SNAP_MINUTES / 60 * HOUR_PX,
            dragState.origHeight + dy
          );
          const snappedH = Math.round(newHeight / (SNAP_MINUTES / 60 * HOUR_PX)) * (SNAP_MINUTES / 60 * HOUR_PX);
          setDragState((prev) => ({ ...prev, currentHeight: snappedH, hasMoved: prev.hasMoved || hasMoved }));
        }
      }

      if (createDrag) {
        const grid = gridRef.current;
        if (!grid) return;
        const dayCol = grid.querySelectorAll("[data-day-col]")[createDrag.dayIdx];
        if (!dayCol) return;
        const rect = dayCol.getBoundingClientRect();
        const scrollTop = dayCol.closest('[data-scroll-container]')?.scrollTop || 0;
        const y = e.clientY - rect.top + scrollTop;
        setCreateDrag((prev) => ({ ...prev, currentY: y }));
      }
    };

    const handleMouseUp = async (e) => {
      if (dragState) {
        const { entry, type, currentTop, currentHeight, currentDayIdx, origTop, origHeight, origDayIdx, hasMoved } = dragState;

        // If user didn't move (just clicked), treat as entry click to open edit dialog
        if (!hasMoved) {
          setDragState(null);
          onEntryClick(entry);
          return;
        }

        // Only update if something changed
        if (currentTop !== origTop || currentHeight !== origHeight || currentDayIdx !== origDayIdx) {
          const newStartTime = yToTime(currentTop);
          const endY = type === "resize" ? currentTop + currentHeight : currentTop + origHeight;
          const newEndTime = yToTime(endY);

          const targetDay = days[currentDayIdx];
          const newStart = new Date(targetDay);
          newStart.setHours(newStartTime.hours, newStartTime.minutes, 0, 0);
          const newEnd = new Date(targetDay);
          newEnd.setHours(newEndTime.hours, newEndTime.minutes, 0, 0);

          if (newEnd > newStart) {
            await onEntryUpdate(entry.id, {
              start_time: newStart.toISOString(),
              end_time: newEnd.toISOString(),
            });
          }
        }
        setDragState(null);
      }

      if (createDrag) {
        const { dayIdx, startY, currentY } = createDrag;
        const minY = Math.min(startY, currentY);
        const maxY = Math.max(startY, currentY);
        const diff = maxY - minY;

        // Only create if dragged at least 15 min worth
        if (diff >= SNAP_MINUTES / 60 * HOUR_PX * 0.8) {
          const startTime = yToTime(minY);
          const endTime = yToTime(maxY);
          const targetDay = days[dayIdx];

          const newStart = new Date(targetDay);
          newStart.setHours(startTime.hours, startTime.minutes, 0, 0);
          const newEnd = new Date(targetDay);
          newEnd.setHours(endTime.hours, endTime.minutes, 0, 0);

          if (newEnd > newStart) {
            onEntryCreate(newStart, newEnd);
          }
        }
        setCreateDrag(null);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState, createDrag, days, onEntryUpdate, onEntryCreate]);

  // ─── Render ─────────────────────────────────────────
  const timeLabels = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) {
    timeLabels.push(h);
  }

  const now = new Date();
  const nowY = timeToY(now);
  const isCurrentWeekDay = (day) => isSameDay(day, now);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="week-calendar">
      {/* Day headers */}
      <div className="grid border-b border-gray-200 sticky top-0 bg-white z-10" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
        <div className="p-2 border-r border-gray-100" /> {/* time label spacer */}
        {days.map((day, idx) => {
          const isToday = isSameDay(day, now);
          return (
            <div key={idx} className={`p-2 text-center border-r border-gray-100 last:border-r-0 ${isToday ? "bg-emerald-50" : ""}`}>
              <div className={`text-xs font-medium ${isToday ? "text-emerald-600" : "text-gray-500"}`}>
                {format(day, "EEE", { locale: de })}
              </div>
              <div className={`text-lg font-bold ${isToday ? "text-emerald-700 bg-emerald-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto" : "text-gray-800"}`}>
                {format(day, "d")}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="overflow-y-auto" style={{ maxHeight: "600px" }} data-scroll-container ref={gridRef}>
        <div className="grid relative" style={{ gridTemplateColumns: "48px repeat(7, 1fr)", height: `${GRID_HEIGHT}px` }}>
          {/* Time labels */}
          <div className="relative border-r border-gray-100">
            {timeLabels.map((h) => (
              <div
                key={h}
                className="absolute w-full text-right pr-2 text-xs text-gray-400 font-mono -translate-y-1/2"
                style={{ top: `${(h - START_HOUR) * HOUR_PX}px` }}
              >
                {fmtTime(h, 0)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, dayIdx) => (
            <div
              key={dayIdx}
              data-day-col
              className={`relative border-r border-gray-100 last:border-r-0 ${isSameDay(day, now) ? "bg-emerald-50/30" : ""}`}
              onMouseDown={(e) => handleGridMouseDown(e, dayIdx)}
              style={{ cursor: createDrag ? "crosshair" : "default" }}
            >
              {/* Hour lines */}
              {timeLabels.map((h) => (
                <div key={h}>
                  <div className="absolute w-full border-t border-gray-100" style={{ top: `${(h - START_HOUR) * HOUR_PX}px` }} />
                  <div className="absolute w-full border-t border-gray-50 border-dashed" style={{ top: `${(h - START_HOUR) * HOUR_PX + HALF_HOUR_PX}px` }} />
                </div>
              ))}

              {/* Current time line */}
              {isSameDay(day, now) && nowY >= 0 && nowY <= GRID_HEIGHT && (
                <div className="absolute w-full z-20 pointer-events-none" style={{ top: `${nowY}px` }}>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1" />
                    <div className="flex-1 border-t-2 border-red-500" />
                  </div>
                </div>
              )}

              {/* Entries */}
              {dayEntries[dayIdx].map((entry) => {
                const color = getColor(entry.project_id);
                const isDragging = dragState?.entryId === entry.id;
                const isBeingDragged = isDragging && dragState.type === "move" && dragState.currentDayIdx !== dayIdx;

                // If this entry is being moved to a different day, hide it here
                if (isBeingDragged) return null;

                const top = isDragging ? dragState.currentTop : entry._top;
                const height = isDragging ? dragState.currentHeight : entry._height;

                return (
                  <div
                    key={entry.id}
                    className={`absolute left-1 right-1 rounded-md border-l-4 px-1.5 py-0.5 cursor-grab select-none overflow-hidden transition-shadow ${color.bg} ${color.border} ${color.text} ${isDragging ? "shadow-lg ring-2 ring-emerald-400 z-30 opacity-90" : "z-10 hover:shadow-md"}`}
                    style={{ top: `${top}px`, height: `${height}px`, minHeight: "20px" }}
                    onMouseDown={(e) => handleEntryMouseDown(e, entry, dayIdx)}
                    onClick={(e) => { if (!dragState) { e.stopPropagation(); onEntryClick(entry); } }}
                    data-testid={`cal-entry-${entry.id}`}
                  >
                    <div className="text-xs font-semibold truncate leading-tight">{entry.project_name}</div>
                    {height > 30 && (
                      <div className="text-[10px] truncate opacity-80">{entry.description || ""}</div>
                    )}
                    {height > 45 && (
                      <div className="text-[10px] font-mono opacity-60">
                        {format(entry._start, "HH:mm")} – {format(entry._end, "HH:mm")}
                      </div>
                    )}
                    {/* Resize handle */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize hover:bg-black/10 rounded-b-md"
                      onMouseDown={(e) => handleResizeMouseDown(e, entry, dayIdx)}
                    />
                  </div>
                );
              })}

              {/* Ghost entry being moved to this day */}
              {dragState?.type === "move" && dragState.currentDayIdx === dayIdx && dragState.origDayIdx !== dayIdx && (
                <div
                  className="absolute left-1 right-1 rounded-md border-l-4 border-dashed px-1.5 py-0.5 z-30 opacity-70 bg-gray-200 border-gray-400"
                  style={{ top: `${dragState.currentTop}px`, height: `${dragState.currentHeight}px` }}
                >
                  <div className="text-xs font-semibold truncate text-gray-600">{dragState.entry.project_name}</div>
                </div>
              )}

              {/* Drag-to-create ghost */}
              {createDrag?.dayIdx === dayIdx && (
                (() => {
                  const minY = Math.min(createDrag.startY, createDrag.currentY);
                  const maxY = Math.max(createDrag.startY, createDrag.currentY);
                  const h = maxY - minY;
                  if (h < 5) return null;
                  const startT = yToTime(minY);
                  const endT = yToTime(maxY);
                  return (
                    <div
                      className="absolute left-1 right-1 rounded-md bg-emerald-200/60 border border-emerald-400 border-dashed z-20 flex items-center justify-center"
                      style={{ top: `${minY}px`, height: `${h}px` }}
                    >
                      <span className="text-xs text-emerald-700 font-medium">
                        {fmtTime(startT.hours, startT.minutes)} – {fmtTime(endT.hours, endT.minutes)}
                      </span>
                    </div>
                  );
                })()
              )}
            </div>
          ))}

          {/* Ghost entry on different day during move */}
          {dragState?.type === "move" && dragState.currentDayIdx === dragState.origDayIdx && (
            <div
              className={`absolute rounded-md border-l-4 px-1.5 py-0.5 z-30 shadow-lg ring-2 ring-emerald-400 opacity-90 ${getColor(dragState.entry.project_id).bg} ${getColor(dragState.entry.project_id).border}`}
              style={{
                top: `${dragState.currentTop}px`,
                height: `${dragState.currentHeight}px`,
                left: `calc(48px + ${dragState.currentDayIdx} * ((100% - 48px) / 7) + 4px)`,
                width: `calc((100% - 48px) / 7 - 8px)`,
              }}
            >
              <div className={`text-xs font-semibold truncate ${getColor(dragState.entry.project_id).text}`}>
                {dragState.entry.project_name}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
